import * as React from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { Field, reduxForm } from 'redux-form';
import type { InjectedFormProps } from 'redux-form';

import type { RootState } from 'af-reducers';

import * as FORMS from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import { Button } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import Input from 'af-fields/Input';

import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import * as JobActions from 'af-actions/jobs';

import type { CopyJobFormModel } from './formModel';
import { validate } from './validations';

const getJobCodeDefaultValue = (code: string) => `${code} - copy`;

interface OwnProps {
	showModal: boolean;
	close: () => void;
	jobToCopyId: number;
	jobToCopyCode: string;
}

type FormOwnProps = OwnProps & ConnectedProps<typeof connector> & CustomRouteComponentProps;

type FormProps = InjectedFormProps<CopyJobFormModel, FormOwnProps>;

type Props = FormOwnProps & FormProps;

const CopyModal: React.FC<Props> = (props) => {
	const {
		showModal,
		close,
		copyJob,
		jobToCopyId,
		jobToCopyCode,
		history,
		companyName,
		location: { state: { orgAlias, originUrl } },
		handleSubmit,
		submitting,
		invalid,
		initialize,
	} = props;

	const resetForm = React.useCallback(() => {
		initialize({ jobCode: getJobCodeDefaultValue(jobToCopyCode) });
	}, [jobToCopyCode, initialize]);

	React.useEffect(() => {
		if (!jobToCopyId || !jobToCopyCode) {
			return;
		}

		resetForm();
	}, [jobToCopyId, jobToCopyCode, resetForm]);

	const closeModal = React.useCallback(() => {
		close();
		resetForm();
	}, [resetForm, close]);

	const onSave = React.useCallback(async (form: CopyJobFormModel) => {
		const job = await copyJob(jobToCopyId, form.jobCode, form.shouldCopyAttachments);

		if (!job) {
			return;
		}

		history.push(originUrl || CLIENT.COMPANY.JOBS.PREVIEW(orgAlias, companyName, job.id.toString()));
		closeModal();
	}, [copyJob, jobToCopyId, closeModal, history, orgAlias, companyName, originUrl]);

	const handleSave = React.useMemo(() => handleSubmit(onSave), [handleSubmit, onSave]);

	if (!jobToCopyId || !jobToCopyCode) {
		return null;
	}

	return (
		<CustomModal
			closeModal={closeModal}
			showModal={showModal}
			size="md"
		>
			<CustomModal.Header
				closeModal={closeModal}
				title="Copy Job"
			/>
			<CustomModal.Body>
				<Form>
					<Row>
						<Col sm={24}>
							<Field
								component={Input}
								isRequired={true}
								label="Job ID / Name"
								name="jobCode"
								type="text"
							/>
						</Col>
					</Row>
				</Form>
				<Row className="row--non-padded">
					<Col md={24}>
						<Field
							component={Checkbox}
							id="shouldCopyAttachments"
							isStandalone={true}
							label="Copy Job Attachments"
							name="shouldCopyAttachments"
							propName="shouldCopyAttachments"
						/>
					</Col>
				</Row>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={closeModal}
					style="secondary"
				/>
				<SubmitButton
					disabled={invalid}
					label="Save"
					onClick={handleSave}
					reduxFormSubmitting={submitting}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		copyJob: JobActions.copyJob,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	withRouter,
	connector,
	reduxForm<CopyJobFormModel, FormOwnProps>({ form: FORMS.JOB_COPY, validate }),
	React.memo
);

export default enhance(CopyModal);
